<template>
	<div>
		<div v-if="hasContent" class="content">
			<slot></slot>
		</div>
		<b-notification v-if="errorMessage" type="is-danger">
			<span v-html="errorMessage" />
		</b-notification>

		<div v-if="featureImage">
			<div class="content">
				<h3 class="title">Featured image</h3>
				<p>
					This is the main photo for your listing and will appear in
					search results. Choose a photo that makes a great first
					impression
				</p>
			</div>
			<div class="featured-image" :class="{ sorting: sortImages }">
				<feature-image
					:name="featureImage"
					:feature="true"
					:on-delete="() => deleteImage(0)"
					class="featured-image-holder"
				></feature-image>
			</div>

			<hr />
		</div>

		<div class="level">
			<div class="level-left">
				<div class="content">
					<h3 v-if="!sortImages" class="title is-marginless">
						Gallery images
					</h3>
					<h3 v-else class="title is-marginless">
						Drag n drop the images to change order
					</h3>
				</div>
			</div>
			<div class="level-right">
				<b-switch v-model="sortImages">Sort images</b-switch>
			</div>
		</div>

		<div v-if="!sortImages">
			<div class="columns is-multiline">
				<gallery-image
					v-for="(_name, index) in galleryImages"
					:key="index"
					:name="_name"
					class="column is-4"
					:cropped="true"
					size="is-4by3"
					:on-delete="() => deleteImage(index + 1)"
				/>
				<div v-if="progress" :class="['column', 'is-4']">
					<progress class="dc-progress" :value="progress" max="100">
						{{ progress }}%
					</progress>
				</div>
				<div
					v-show="galleryImages.length <= 8"
					:class="['column', 'is-4']"
				>
					<dropzone
						:id="name"
						ref="dcGalleryDropzone"
						:options="options"
						:class="{ 'is-active': galleryImages.length }"
						@vdropzone-error="uploadError"
						@vdropzone-error-multiple="uploadError"
						@vdropzone-success="success"
						@vdropzone-total-upload-progress="upload"
						@vdropzone-queue-complete="complete"
					/>
				</div>
			</div>
			<p v-if="showError" class="help is-danger">{{ error }}</p>
			<div class="my-preview"></div>
		</div>
		<div v-if="sortImages">
			<draggable
				:list="myFields"
				ghost-class="ghost"
				class="columns is-multiline image-sorting"
				@end="end"
			>
				<gallery-image
					v-for="(_name, index) in myFields"
					:key="index"
					:name="_name"
					class="column is-4"
					:cropped="true"
					size="is-4by3"
					sortable
				/>
			</draggable>
		</div>
	</div>
</template>

<script>
import Dropzone from "vue2-dropzone";
import fieldArrayMixin from "@lab/utils/fieldArrayMixin";
import FeatureImage from "./FeatureImage";
import GalleryImage from "./GalleryImage";
import Draggable from "vuedraggable";

export default {
	components: { Dropzone, GalleryImage, FeatureImage, Draggable },
	mixins: [fieldArrayMixin],
	data() {
		return {
			images: [],
			progress: null,
			errorMessage: null,
			sortImages: false
		};
	},
	computed: {
		featureImage: {
			get() {
				return this.myFields.slice(0, 1).shift();
			},
			set() {
				// Updating is handled via 'end'
			}
		},
		galleryImages: {
			get() {
				return this.myFields.slice(1);
			},
			set() {
				// Updating is handled via 'end'
			}
		},

		options() {
			return {
				url: process.env.VUE_APP_SERVICES + "/media/image",
				headers: {
					Authorization:
						"BEARER " + window.localStorage.getItem("token")
				},
				dictDefaultMessage:
					"<i class='fa fa-cloud-upload'></i> Click or drag files to upload",
				dictFileTooBig:
					"<strong>It looks like you've selected a very large image to upload ({{filesize}}MB)</strong><br/>Please check that the image file size is less than {{maxFilesize}}MB",
				acceptedFiles: "image/*",
				thumbnailWidth: null,
				thumbnailHeight: 120,
				maxFilesize: 1.5,
				maxFiles: 10,
				thumbnailMethod: "contain",
				addRemoveLinks: true,
				previewsContainer: ".my-preview"
			};
		},
		hasContent() {
			return !!this.$slots.default;
		}
	},
	methods: {
		uploadError(file, message) {
			this.errorMessage = message;
		},
		success(file, response) {
			if (response["url"]) {
				this.fieldPush(response);
			}
		},
		upload(totaluploadprogress) {
			this.progress = totaluploadprogress;
		},
		complete() {
			this.progress = null;
		},
		deleteImage(index) {
			this.confirm(
				`Delete image?`,
				`Are you sure you would like to remove the image?`,
				// what type of message to display
				"is-warning",
				// Label to use on the confirm button
				"Confirm deletion",
				// function to run if confirm button is clicked
				() => this.fieldRemove(index)
			);
		},
		columnWidth(index) {
			return ["listing-image", "column", index === 0 ? "is-12" : "is-4"];
		},
		end(change) {
			this.fieldMove(change.oldIndex, change.newIndex);
		}
	}
};
</script>

<style lang="scss">
.featured-image {
	position: relative;
	&.sorting {
		.featured-image-holder::after {
			content: "";
			position: absolute;
			background-color: rgba(white, 0.8);
			left: 0;
			right: 0;
			top: 0;
			bottom: 0;
		}
	}
}
.image-sorting {
	.ghost {
		.image-panel {
			border: 2px dashed #cdcdcd;

			* {
				visibility: hidden;
			}
		}
	}
}
</style>

<style lang="scss" scoped>
.dropzone {
	min-height: auto;
	border-width: 1px;
	padding-top: 53.3%;
	position: relative;
	font-family: "proxima-nova", sans-serif;
	&.is-active {
		padding-top: 66%;
	}
}

.dc-progress {
	-moz-appearance: none;
	-webkit-appearance: none;
	border: none;
	display: block;
	height: 100%;
	overflow: hidden;
	width: 100%;

	&::-webkit-progress-bar {
		background-color: #dbdbdb;
	}
	&::-webkit-progress-value {
		background-color: #4a4a4a;
	}
	&::-moz-progress-bar {
		background-color: #4a4a4a;
	}
	&::-ms-fill {
		background-color: #4a4a4a;
		border: none;
	}

	&::-webkit-progress-value {
		background-color: #00d1b2;
	}
	&::-moz-progress-bar {
		background-color: #00d1b2;
	}
	&::-ms-fill {
		background-color: #00d1b2;
	}
}

.my-preview {
	display: none;
}
</style>

<style scoped>
.dropzone >>> .dz-message {
	padding-top: 27.125%;
	line-height: 1;
	position: absolute;
	top: 0;
	left: 0;
	bottom: 0;
	right: 0;
	margin: 0;
}
.is-active >>> .dz-message {
	padding: 29.125% 18%;
}
</style>
